// modules/sync/index.ts — Platik-IA
// Motor de sincronización: IndexedDB ↔ Google Sheets.
// Procesa la cola en background; usa Service Worker waitUntil si disponible.

import { bus }       from '../../core/event-bus';
import { getModule } from '../../core/registry';
import {
  getPendingSyncItems, removeSyncItem, incrementSyncRetry, resetSyncRetries,
  putTransaction, getAllTransactions, deleteTransaction, putCategories,
} from '../../services/idb';
import {
  appendTransaction, updateTransactionById, deleteTransactionById,
  readAllTransactions, createSpreadsheet,
} from '../../services/google-sheets';
import type { SyncAPI }      from './contracts';
import type { IDBSyncQueueItem } from '../../services/idb';

const MAX_RETRIES   = 3;
let   _flushTimer:  ReturnType<typeof setTimeout> | null = null;
let   _isFlushing   = false;

function getAuth() {
  return getModule<{
    getUser: () => { sheetId: string | null; id: string } | null;
    getAccessToken: () => Promise<string | null>;
  }>('auth');
}

/** Marca la transacción asociada como 'error' en IDB (sin tocar la cola). */
async function markTxError(entityId: string): Promise<void> {
  const allTx = await getAllTransactions();
  const tx = allTx.find(t => t.id === entityId);
  if (tx && tx.syncStatus !== 'error') {
    await putTransaction({ ...tx, syncStatus: 'error' });
  }
}

async function processItem(item: IDBSyncQueueItem, token: string, sheetId: string): Promise<void> {
  if (item.entityType === 'transaction') {
    const payload = item.payload as Parameters<typeof appendTransaction>[2];
    if (item.operation === 'create') {
      await appendTransaction(token, sheetId, payload);
    } else if (item.operation === 'update') {
      await updateTransactionById(token, sheetId, payload);
    } else if (item.operation === 'delete') {
      await deleteTransactionById(token, sheetId, item.entityId);
    }
  }
}

export async function initSync(): Promise<SyncAPI> {
  // Escuchar cuando el usuario se loguea → arrancar sync
  bus.on('auth:signed-in', async ({ user }) => {
    // Sesión nueva: dar otra oportunidad a items que se quedaron atascados antes.
    await resetSyncRetries().catch(console.warn);

    if (!user.sheetId) {
      // Primera vez: crear el spreadsheet
      const token = await getAuth()?.getAccessToken();
      if (token) {
        try {
          const cats = getModule<{ getAll: () => Record<string, string[]> }>('categories')?.getAll() ?? {};
          const newSheetId = await createSpreadsheet(token, 'Ingresos y gastos IA', cats);
          // Actualizar sheetId en Supabase y en el módulo auth
          const { supabase } = await import('../../services/supabase');
          await supabase.from('users').update({ sheet_id: newSheetId }).eq('id', user.id);
          user.sheetId = newSheetId;
          bus.emit('notifications:show', { message: 'Hoja de cálculo creada en tu Drive ✓', type: 'success' });
        } catch (err) {
          console.error('[sync] Error creando spreadsheet:', err);
        }
      }
    }
    // Al conectarse: empujar pendientes y LUEGO traer cambios remotos (multi-dispositivo).
    setTimeout(async () => {
      await api.flush();
      await api.fullSync({ silent: true });
    }, 1000);
  });

  // Re-intentar flush al volver la conexión
  window.addEventListener('online', () => api.flush());

  const api: SyncAPI = {
    async flush() {
      if (_isFlushing || !navigator.onLine) return;

      const auth  = getAuth();
      const user  = auth?.getUser();
      if (!user?.sheetId) return;
      const token = await auth?.getAccessToken();
      if (!token) return;   // sin token válido: dejamos los items en cola para reintentar

      _isFlushing = true;
      bus.emit('sync:started');

      const items = await getPendingSyncItems();
      let synced = 0;

      for (const item of items) {
        // Items que agotaron los reintentos: NO se borran (se conservan para una
        // futura sesión que resetea retries). Se saltan y se marcan en error.
        if (item.retries >= MAX_RETRIES) {
          await markTxError(item.entityId);
          bus.emit('sync:status-changed', { id: item.entityId, status: 'error' });
          continue;
        }

        try {
          bus.emit('sync:status-changed', { id: item.entityId, status: 'syncing' });
          await processItem(item, token, user.sheetId);
          await removeSyncItem(item.id);

          // Marcar como synced en IDB
          const allTx = await getAllTransactions();
          const tx = allTx.find(t => t.id === item.entityId);
          if (tx) await putTransaction({ ...tx, syncStatus: 'synced', syncedAt: new Date().toISOString() });

          bus.emit('sync:status-changed', { id: item.entityId, status: 'synced' });
          synced++;
        } catch (err) {
          console.warn(`[sync] Error en item ${item.id}:`, err);
          await incrementSyncRetry(item.id);
          await markTxError(item.entityId);
          bus.emit('sync:status-changed', { id: item.entityId, status: 'error' });
        }
      }

      _isFlushing = false;
      bus.emit('sync:completed', { synced });
    },

    async fullSync(opts: { silent?: boolean } = {}) {
      const { silent = false } = opts;
      const auth  = getAuth();
      const user  = auth?.getUser();
      if (!user?.sheetId) return;
      const token = await auth?.getAccessToken();
      if (!token) return;

      bus.emit('sync:started');
      if (!silent) bus.emit('notifications:show', { message: 'Sincronizando datos desde Drive…', type: 'info' });

      try {
        // readAllTransactions lanza ante error de API → un fallo transitorio NO llega
        // a la reconciliación de borrados de abajo (no se borra nada por accidente).
        const sheetTxs = await readAllTransactions(token, user.sheetId);
        const sheetIds = new Set(sheetTxs.map(s => s.id));

        const local = await getAllTransactions();
        // No pisar items locales aún sin sincronizar (pending/syncing/error):
        // sus ediciones podrían no estar todavía reflejadas en la hoja.
        const localPending = new Set(
          local.filter(t => t.syncStatus !== 'synced').map(t => t.id)
        );

        // Propagación de borrados: un movimiento local 'synced' que ya NO está en la
        // hoja fue eliminado en otro dispositivo → borrarlo también acá (sin re-encolar).
        let removed = 0;
        for (const t of local) {
          if (t.syncStatus === 'synced' && !sheetIds.has(t.id)) {
            await deleteTransaction(t.id);
            removed++;
          }
        }

        let merged = 0;
        for (const stx of sheetTxs) {
          if (localPending.has(stx.id)) continue;
          await putTransaction({
            id:           stx.id,
            fecha:        stx.fecha,
            tipo:         stx.tipo,
            monto:        stx.monto,
            descripcion:  stx.descripcion,
            categoria:    stx.categoria,
            subcategoria: stx.subcategoria,
            syncStatus:   'synced',
            syncedAt:     new Date().toISOString(),
            createdAt:    stx.fecha,
          });
          merged++;
        }

        if (removed) console.info(`[sync] ${removed} movimiento(s) borrado(s) en otro dispositivo, removidos localmente`);
        bus.emit('sync:completed', { synced: merged });
        if (!silent) bus.emit('notifications:show', { message: `${merged} movimientos importados ✓`, type: 'success' });
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Error desconocido';
        bus.emit('sync:error', { message: msg });
        if (!silent) bus.emit('notifications:show', { message: `Error de sync: ${msg}`, type: 'error' });
      }
    },

    dispose() {
      if (_flushTimer) clearTimeout(_flushTimer);
      window.removeEventListener('online', api.flush);
    },
  };

  bus.emit('sync:ready');
  return api;
}
